/* eslint-disable camelcase */
import React from "react";

import { Link } from "react-router-dom";
import { Grid } from "@mui/material";
import { FormikContextType, useFormikContext } from "formik";
import { FormData, FormData_layouts_groups as LayoutGroup } from "types/gql-types";

import { Button } from "components/haven-design";
import { FormStatus as FormStatusType, FormValues } from "pages/eligibility/type";
import SaveProgress from "pages/eligibility/components/context/save-progress";
import { getLayout, getSectionIndex, keyFromIdentity } from "../helpers";
import styles from "./section.module.css";

import FormStatus from "./form-status";

const hasNotAgreedToTerms = (form: FormikContextType<FormValues>):boolean =>
  form.values[keyFromIdentity(["agree_to_terms", "applicant"])] !== "true";
const isSubmitDisabled = (form: FormikContextType<FormValues>) => form.isSubmitting ||
  hasNotAgreedToTerms(form);

const getSiblings = (groups: LayoutGroup[], sectionIndex: number) => {
  const prev = groups[Math.max(0, sectionIndex - 1)];
  const next = groups[Math.min(groups.length - 1, sectionIndex + 1)];
  return { prev, next };
};

export type MobileMenu = {
  isMobile: boolean;
  toggle: () => void;
}

type NavigationProps = {
  form: FormData,
  groups: LayoutGroup[],
  section: string | undefined,
  mobileMenu: MobileMenu,
} & {path: { match?: { submissionId?: string } }};

const FormNavigation =
// eslint-disable-next-line max-lines-per-function
(
  { groups, path, section, form, mobileMenu }: NavigationProps,
): React.ReactElement => {
  const layout = getLayout(form);
  const sectionIndex = getSectionIndex(section, layout);
  const formContext = useFormikContext<FormValues>();
  React.useEffect(() => {
    formContext.setStatus(
      Object.keys(formContext.errors).length ? FormStatusType.ValidationError : null,
    );
  }, [formContext.errors]);
  const saveProgress = React.useContext(SaveProgress);
  if (sectionIndex === groups.length - 1) {
    return (
      <Grid size={{ xs: 12 }}>
        <hr />
        <FormStatus
          form={form}
          showCertificationWarning={hasNotAgreedToTerms(formContext)}
          mobileMenu={mobileMenu} />
        <Button
          form="full-app-form"
          color="primary"
          fullWidth
          type="submit"
          disabled={isSubmitDisabled(formContext) ||
            !!Object.keys(formContext.errors).length}
          onClick={saveProgress}
        >
          {form.skipSignature ? "Submit Application" : "Sign Application"}
        </Button>
      </Grid>
    );
  }
  const { prev, next } = getSiblings(groups, sectionIndex);
  return (
    <Grid size={{ xs: 12 }}>
      <hr />
      <div className={sectionIndex === 0 ? styles["navigation-start"] : styles.navigation}>
        {sectionIndex > 0 && (
          <Link to={`/application/${path.match?.submissionId}/${prev.name}`}
            style={{ marginRight: 8 }}
          >
            <Button color="secondary" fullWidth type="submit">
              Previous</Button>
          </Link>
        )}
        <Link to={`/application/${path.match?.submissionId}/${next.name}`}>
          <Button
            color="primary"
            fullWidth
            type="submit"
            onClick={saveProgress}
          >
            {sectionIndex === 0 ? "Start" : "Save & Continue"}</Button>
        </Link>
      </div>
    </Grid>
  );
};

export default FormNavigation;
